import React, { useContext, useState, useEffect, useMemo } from "react";
import { Phone, ArrowUp, ArrowDown, Pen } from "lucide-react";
import SharedDataContext from "../../../SharedDataContext";
import { useTableData } from "../../../hooks/useTableData";
import {
  getAllPhoneNumbers,
  getAccessiblePhoneNumbers,
  updatePhoneNumber,
} from "../../../apis/phoneInventory";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import EditColumn from "../../EditColumn";
import Filter from "../../Filter";
import { returnArray, isEmptyObject } from "../../../utils/GenericFn";
import { Switch } from "../../ui/switch";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { usePhoneInventoryStore } from "../../../stores/usePhoneInventoryStore";
import { Button } from "../../ui/button";
import { Plus } from "lucide-react";

import PurchaseNumberPanel from "./PurchaseNumberPanel";
import FeaturePromotionView from "./FeaturePromotionView";
import PhoneInventoryEmptyState from "./PhoneInventoryEmptyState";
import PageHeader from "../../PageHeader";
import { Badge } from "../../ui/badge";
import Flag from "../../Flag";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function AllNumbers() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const { workspaceSlug } = useParams();
  const navigate = useNavigate();
  const isAdmin = activeWorkspace?.role === "Owner" || activeWorkspace?.role === "Admin";
  const { openPurchaseNumberPanel } = usePhoneInventoryStore();
  const captureEvent = useCaptureEvent();

  const queryClient = useQueryClient();

  const [selectedRows, setSelectedRows] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [updatingNumbers, setUpdatingNumbers] = useState(new Set());
  const queryKeyName = isAdmin ? "get_phone_numbers_admin" : "get_phone_numbers_user";

  const handleOpenPurchasePanel = () => {
    captureEvent("SET_PhoneInventory_PurchasePanelOpen");
    openPurchaseNumberPanel("inventory", {
      returnTo: "inventory",
      workspaceSlug: workspaceSlug,
      sourceComponent: "phone-inventory",
    });
  };

  useEffect(() => {
    const handleInventoryUpdate = (event) => {
      const { number } = event.detail;

      queryClient.invalidateQueries(isAdmin ? "get_phone_numbers_admin" : "get_phone_numbers_user");
      toast.success(`Number ${number.phone_number} added to inventory!`);
    };

    const handleUserSelection = (event) => {
      const { number, user } = event.detail;
      toast.success(`Number ${number.phone_number} selected!`);
    };

    window.addEventListener("numberAddedToInventory", handleInventoryUpdate);
    window.addEventListener("numberSelectedForUser", handleUserSelection);

    return () => {
      window.removeEventListener("numberAddedToInventory", handleInventoryUpdate);
      window.removeEventListener("numberSelectedForUser", handleUserSelection);
    };
  }, [queryClient, isAdmin]);

  const {
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    filters,
    sort,
    sortedVisibleColumns,
    getPageNumber,
    getFiltersObject,
    handleSetSort,
    getColumns,
    query,
  } = useTableData({
    queryKey: queryKeyName,
    fetchFn: isAdmin ? getAllPhoneNumbers : getAccessiblePhoneNumbers,
    defaultHeaders: { "X-Workspace-Id": activeWorkspace?.id },
    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      setIsInitialLoad(false);
    },
    onError: (error) => {
      console.error("Phone Numbers API error:", error);
      setIsInitialLoad(false);
    },
  });

  const updatePhoneNumberMutation = useMutation(updatePhoneNumber, {
    onSuccess: (data, variables) => {
      setUpdatingNumbers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.uniqueRowId);
        return newSet;
      });

      // Show success toast
      toast.success(
        `Recording ${variables.data.recording_enabled ? "enabled" : "disabled"} for phone number`
      );

      // Optional: Invalidate cache after a delay to allow backend to process
      // This prevents race conditions with optimistic updates
      queryClient.invalidateQueries(queryKeyName);
      captureEvent("SET_PhoneInventory_RecordingToggleSuccess", {
        phone_number_id: variables.uniqueRowId,
        enabled: variables.data.recording_enabled,
      });
    },
    onError: (error, variables) => {
      console.error("Error updating phone number:", error);

      setUpdatingNumbers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.uniqueRowId);
        return newSet;
      });

      // Show error toast
      toast.error("Failed to update recording setting. Please try again.");
      captureEvent("SET_PhoneInventory_RecordingToggleFailure", {
        phone_number_id: variables.uniqueRowId,
        enabled: variables.data.recording_enabled,
        error: error?.message || "unknown_error",
      });
    },
  });

  const mappedNumbers = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item, index) => {
      const originalId = item.id;
      const uniqueId = `${originalId}-${item.phone_number}-${item.type || "unknown"}-${item.tag || "notag"}-${index}`;

      return {
        ...item,
        originalId: originalId, // Store original ID for API calls
        id: uniqueId, // Use unique ID for React rendering and state management
        // Transform assigned users for display
        assigned_users_display: Array.isArray(item.assigned_users) ? item.assigned_users : [],
        assigned_users_names: Array.isArray(item.assigned_users)
          ? item.assigned_users.map((user) => user.name).join(", ")
          : item.assigned_users === "null"
            ? "None"
            : "None",
        // Transform capabilities for display
        capabilities_list: item.capabilities
          ? Object.entries(item.capabilities)
              .filter(([key, value]) => value)
              .map(([key]) => key.toUpperCase())
              .join(", ")
          : "None",
        // Keep raw capabilities for badge rendering
        capabilities: item.capabilities || {},
        // Format location
        location_display: [item.locality, item.region, item.country].filter(Boolean).join(", "),
        // Status based on tag or type
        status_display:
          item.tag && item.tag !== "null" ? item.tag : item.type === "Assigned" ? "Active" : "Available",
      };
    });
  }, [data]);

  // Handle workspace changes
  useEffect(() => {
    setIsInitialLoad(true);
  }, [workspaceSlug]);

  useEffect(() => {
    if (activeWorkspace?.id) {
      captureEvent("SET_PhoneInventory_PageView", {
        workspace_id: activeWorkspace.id,
        role: activeWorkspace?.role,
      });
    }
  }, [activeWorkspace?.id, activeWorkspace?.role, captureEvent]);

  const handleSelectAll = () => {
    setSelectedRows(selectedRows.length === mappedNumbers?.length ? [] : mappedNumbers.map((item) => item.id));
  };

  const handleSelectRow = (isChecked, id) => {
    setSelectedRows((prev) => (isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)));
  };

  const renderAssignedUsers = (assignedUsers) => {
    const users = Array.isArray(assignedUsers) ? assignedUsers : [];
    if (users.length === 0) return <span className="text-gray-400">None</span>;

    const visibleCount = users.length > 2 ? 2 : users.length;
    const visibleUsers = users.slice(0, visibleCount);
    const hiddenUsers = users.slice(visibleCount);

    return (
      <div className="flex items-center gap-1">
        {/* <Users className="h-4 w-4 text-gray-500" /> */}
        <div className="flex flex-wrap gap-1 items-center">
          {visibleUsers.map((user, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
            >
              {user.name}
            </span>
          ))}
          {hiddenUsers.length > 0 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
              +{hiddenUsers.length} more
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderStatus = (item) => {
    const status = item.status_display;
    let className = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ";

    switch (status?.toLowerCase()) {
      case "active":
        className += "bg-green-100 text-green-800";
        break;
      case "validation pending":
        className += "bg-yellow-100 text-yellow-800";
        break;
      case "inactive":
        className += "bg-gray-100 text-gray-800";
        break;
      default:
        className += "bg-blue-100 text-blue-800";
    }

    return <span className={className}>{status}</span>;
  };

  const handleRecordingToggle = (phoneNumberId, currentStatus) => {
    setUpdatingNumbers((prev) => new Set(prev.add(phoneNumberId)));

    const item = mappedNumbers.find((item) => item.id === phoneNumberId);
    const originalApiId = item?.originalId || phoneNumberId;

    captureEvent("SET_PhoneInventory_RecordingToggleClick", {
      phone_number_id: originalApiId,
      enabled: !currentStatus,
    });
    updatePhoneNumberMutation.mutate({
      phoneNumberId: originalApiId,
      uniqueRowId: phoneNumberId,
      data: { recording_enabled: !currentStatus },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const handleRowClick = (phoneNumber) => {
    const originalId = phoneNumber.originalId || phoneNumber.id;
    navigate(`/${workspaceSlug}/numbers-inventory/${originalId}/settings`);
  };

  const renderCapabilities = (capabilities) => {
    if (!capabilities) return <span className="text-gray-400">-</span>;

    const capabilityLabels = {
      voice: "Call",
      sms: "SMS",
      mms: "MMS",
      fax: "Fax",
    };

    return (
      <div className="flex flex-wrap gap-1">
        {Object.entries(capabilities).map(([key, isActive]) => {
          const label = capabilityLabels[key] || key;
          return (
            <Badge key={key} variant={isActive ? "indigo" : "gray"}>
              {isActive && <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5 flex-shrink-0"></span>}
              {label}
            </Badge>
          );
        })}
      </div>
    );
  };

  const renderRecordingEnabled = (item) => {
    const isUpdating = updatingNumbers.has(item.id);

    return (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={item.recording_enabled}
          onCheckedChange={() => handleRecordingToggle(item.id, item.recording_enabled)}
          disabled={isUpdating || !isAdmin}
          className={`data-[state=checked]:bg-[#7F56D9] data-[state=unchecked]:bg-gray-300 ${
            isUpdating ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />

        {isUpdating && (
          <div className="inline-flex items-center">
            <div className="loader loader_circle w-3 h-3"></div>
          </div>
        )}
      </div>
    );
  };

  const renderCellContent = (columnKey, item) => {
    switch (columnKey) {
      case "phone_number":
        return (
          <div className="flex items-center">
            <span className="text-[40px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border  mr-2">
              <Flag code={item?.country} squared />
            </span>
            <div className="">
              {item.label ? (
                <div>
                  <div className="font-semibold text-[#101828] dark:text-white text-sm capitalize">
                    {item.label}
                  </div>
                  <div className="text-sm text-[#535862] dark:text-gray-400 font-normal mt-1">
                    {item.phone_number} <span className="text-[11px]">({item.type})</span>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-[#101828] dark:text-white text-sm">
                    {item.phone_number}
                  </div>
                  <div className="text-sm text-[#535862] dark:text-gray-400 font-normal">({item.type})</div>
                </div>
              )}
            </div>
          </div>
        );

      case "number_type":
        return (
          <div>
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-background text-gray-600 border border-border shadow-sm">
              {item.number_type}
            </span>
          </div>
        );

      case "status":
        return renderCapabilities(item.capabilities);

      case "assigned_users":
        if (item?.type === "Shared") {
          return <span className="text-gray-700">Across workspace</span>;
        } else {
          return renderAssignedUsers(item.assigned_users_display);
        }

      case "recording_enabled":
        return renderRecordingEnabled(item);

      case "capabilities":
        return renderCapabilities(item.capabilities);

      default:
        return item?.[columnKey] || "-";
    }
  };

  const hasFilters = !isEmptyObject(filters);
  const hasData = Array.isArray(mappedNumbers) && mappedNumbers.length > 0;
  const baseEmpty = !isFetching && isSuccess && !isInitialLoad && !hasData;
  const shouldShowFilteredEmptyState = baseEmpty && hasFilters;
  const shouldShowEmptyState = baseEmpty && !hasFilters;

  if (query?.error?.status === 403 || query?.error?.response?.status === 403) {
    return (
      <>
        <PageHeader heading="Phone numbers" description="Add new or manage existing phone numbers" />
        <FeaturePromotionView />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        heading="Phone numbers"
        description="Add new or manage existing phone numbers"
        cta={
          <>
            <div>
              <Button
                onClick={handleOpenPurchasePanel}
                className="bg-[#7F56D9] hover:bg-purple-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add new number
              </Button>
            </div>
          </>
        }
      />

      {(hasData || hasFilters) && (
        <div className="flex justify-between items-center">
          <div className="space-x-3 inline-flex items-center">
            {filterMetadata && (
              <>
                <span className="text-sm text-gray-500">Filters:</span>
                <Filter filterMetadata={filterMetadata} getFiltersObject={getFiltersObject} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {columnMetadata && <EditColumn columnMetadata={columnMetadata} getColumns={getColumns} />}
          </div>
        </div>
      )}

      {/* Table */}
      <div>
        {hasData && isSuccess && (
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden border border-border rounded-[--radius]">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-background">
                      <tr>
                        {returnArray(sortedVisibleColumns).map((column, index) => (
                          <th
                            key={index}
                            scope="col"
                            className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                              column.sortable ? "cursor-pointer" : ""
                            }`}
                            onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                          >
                            <span className={"inline-flex items-center"}>
                              {column.display_name}
                              {column.sortable && (
                                <>
                                  {sort === `-${column.key}` ? (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3" />
                                  ) : sort === column.key ? (
                                    <ArrowUp className="inline-block ml-1 h-3 w-3" />
                                  ) : (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                                  )}
                                </>
                              )}
                            </span>
                          </th>
                        ))}
                        <th scope="col" className="!px-3 !py-4 text-left text-sm font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-background">
                      {mappedNumbers &&
                        mappedNumbers.length > 0 &&
                        returnArray(mappedNumbers).map((item, index) => (
                          <tr
                            key={item.id}
                            className={`hover:bg-gray-50 cursor-pointer ${index % 2 !== 0 ? "" : ""}`}
                            onClick={() => handleRowClick(item)}
                          >
                            {returnArray(sortedVisibleColumns).map(({ key: colKey }) => (
                              <td
                                key={colKey}
                                className="whitespace-nowrap px-4 text-sm text-gray-900 dark:text-[#F7F7F7] "
                              >
                                {renderCellContent(colKey, item)}
                              </td>
                            ))}
                            <td className="whitespace-nowrap !px-3 !py-4">
                              <Pen className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600" />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {(isLoading || isInitialLoad) && <TableLoader />}

        {isError && (
          <NoData title="Error loading phone numbers" description="Please try again or contact support" />
        )}

        {shouldShowFilteredEmptyState && (
          <NoData
          className={"flex justify-center items-center flex-col"}
            heading="No phone numbers match your filters"
            description="Try adjusting or clearing filters to see more results."
          />
        )}

        {shouldShowEmptyState && <PhoneInventoryEmptyState />}

        {/* Pagination */}
        <div className="flex items-center justify-between rounded-[--radius] py-3">
          <div className="flex-1">
            {paginationInfo && paginationInfo.total > 0 && <Pagination data={paginationInfo} getPageNumber={getPageNumber} />}
          </div>
        </div>
      </div>

      {/* Purchase Number Panel */}
      <PurchaseNumberPanel />
    </div>
  );
}
